// Regras de personalizacao por categoria (match por nome).
// Usado pelo POS e pela app publica (TenantApp) para abrir um modal
// de opcoes (molho, ponto, acompanhamento, intensidade de cafe) em vez
// de adicionar o item diretamente ao carrinho.

export interface CustomOption {
  id: string;
  label: string;
}

export interface CustomGroup {
  id: string;
  label: string;
  required: boolean;
  multi: boolean;
  options: CustomOption[];
}

export interface CustomRule {
  match: string[]; // palavras-chave no nome da categoria ou do item
  groups: CustomGroup[];
}

export const CUSTOM_RULES: CustomRule[] = [
  {
    match: ['frango', 'churrasco', 'carne', 'grelhada', 'picanha', 'bife', 'prego', 'vitela', 'porco', 'secreto', 'febra', 'entrecosto', 'costoleta'],
    groups: [
      {
        id: 'molho', label: 'Molho', required: false, multi: false,
        options: [
          { id: 'sem', label: 'Sem molho' },
          { id: 'piripiri', label: 'Piri-piri' },
          { id: 'picante', label: 'Picante' },
          { id: 'barbecue', label: 'Barbecue' },
          { id: 'alho', label: 'Alho' },
          { id: 'mostarda', label: 'Mostarda' },
        ],
      },
      {
        id: 'ponto', label: 'Ponto da carne', required: false, multi: false,
        options: [
          { id: 'mal_passado', label: 'Mal passado' },
          { id: 'passado', label: 'Médio' },
          { id: 'bem_passado', label: 'Bem passado' },
        ],
      },
      {
        id: 'acompanhamento', label: 'Acompanhamento', required: false, multi: false,
        options: [
          { id: 'arroz', label: 'Arroz' },
          { id: 'batata_frita', label: 'Batata frita' },
          { id: 'batata_cozida', label: 'Batata cozida' },
          { id: 'salada', label: 'Salada' },
          { id: 'feijao', label: 'Feijão' },
        ],
      },
    ],
  },
  {
    match: ['peixe', 'bacalhau', 'dourada', 'robalo', 'salmao', 'polvo', 'lulas', 'camarao', 'gamba'],
    groups: [
      {
        id: 'molho', label: 'Molho', required: false, multi: false,
        options: [
          { id: 'sem', label: 'Sem molho' },
          { id: 'manteiga', label: 'Manteiga' },
          { id: 'alho', label: 'Alho' },
          { id: 'limao', label: 'Limão' },
          { id: 'picante', label: 'Picante' },
        ],
      },
      {
        id: 'acompanhamento', label: 'Acompanhamento', required: false, multi: false,
        options: [
          { id: 'arroz', label: 'Arroz' },
          { id: 'batata_cozida', label: 'Batata cozida' },
          { id: 'salada', label: 'Salada' },
          { id: 'legumes', label: 'Legumes' },
        ],
      },
    ],
  },
  {
    match: ['menu', 'familiar', 'take-away', 'takeaway', 'ementa', 'combinado'],
    groups: [
      {
        id: 'molho', label: 'Molho', required: false, multi: false,
        options: [
          { id: 'sem', label: 'Sem molho' },
          { id: 'piripiri', label: 'Piri-piri' },
          { id: 'barbecue', label: 'Barbecue' },
          { id: 'alho', label: 'Alho' },
        ],
      },
      {
        id: 'acompanhamento', label: 'Acompanhamento', required: false, multi: false,
        options: [
          { id: 'arroz', label: 'Arroz' },
          { id: 'batata_frita', label: 'Batata frita' },
          { id: 'salada', label: 'Salada' },
        ],
      },
    ],
  },
  {
    match: ['cafe', 'expresso', 'espresso', 'pingado', 'meia de leite', 'meia-de-leite', 'galao', 'cappuccino', 'latte', 'abatanado'],
    groups: [
      {
        id: 'quantidade', label: 'Quantidade', required: false, multi: false,
        options: [
          { id: 'curto', label: 'Curto' },
          { id: 'normal', label: 'Normal' },
          { id: 'cheio', label: 'Cheio' },
        ],
      },
      {
        id: 'acucar', label: 'Açúcar', required: false, multi: false,
        options: [
          { id: 'sem_acucar', label: 'Sem açúcar' },
          { id: 'pouco', label: 'Pouco' },
          { id: 'normal_acucar', label: 'Normal' },
          { id: 'doce', label: 'Doce' },
        ],
      },
    ],
  },
];

// Procura a regra aplicavel comparando palavras-chave com o nome da
// categoria e do item (ambos em minusculas).
export function findCustomRule(categoryName: string | undefined, itemName: string): CustomRule | null {
  if (!categoryName) return null;
  const haystack = `${categoryName} ${itemName}`.toLowerCase();
  for (const rule of CUSTOM_RULES) {
    if (rule.match.some((kw) => haystack.includes(kw))) return rule;
  }
  return null;
}

// Constroi a string de notas a partir das selecoes do modal.
// Retorna undefined quando nenhuma opcao foi escolhida.
export function buildCustomNotes(rule: CustomRule, selections: Record<string, string>): string | undefined {
  const parts: string[] = [];
  for (const group of rule.groups) {
    const sel = selections[group.id];
    if (sel) {
      const opt = group.options.find((o) => o.id === sel);
      if (opt) parts.push(`${group.label}: ${opt.label}`);
    }
  }
  return parts.length > 0 ? parts.join(' | ') : undefined;
}
